import React, { useState } from 'react';
import { Card, Col, Form as ReactForm, Row, Table } from 'react-bootstrap';
import * as Yup from 'yup';
import {
  InternalApiEntityType,
  InternalApiJMESPathExpressionTestResult,
  InternalApiJMESPathExpressionTestType,
} from '../../api-internal';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { jmespathExpressionTestApi } from '../../apis';
import FormField from '../../form/FormField';
import { Button, SalvDropdown, SalvTooltipWrapper } from '@salvapp/ui-components';
import { downloadDocument } from '../../common/document/helper';
import { generateInsertScript } from './ScriptGenerator';
import { parseErrorMessage } from '../../common/errors/helpers';
import SalvPage from '../../common/SalvPage';
import { useMe } from '../../organisation/useMe';

const validationSchema = Yup.object().shape({
  expression: Yup.string().required('Required').min(1, 'Too short'),
  externalId: Yup.string().required('Required'),
  testType: Yup.object().shape({
    key: Yup.string(),
    label: Yup.string().required('Required'),
    value: Yup.string().required('Required'),
  }),
  entityType: Yup.object().shape({
    key: Yup.string(),
    label: Yup.string().required('Required'),
    value: Yup.string().required('Required'),
  }),
});

type TestInputPanelValues = {
  testType: {
    key: string;
    value: InternalApiJMESPathExpressionTestType;
    label: string;
  };
  entityType: {
    key: string;
    value: InternalApiEntityType;
    label: string;
  };
  insertScript: {
    key: string;
    value: string;
    label: string;
    type: string;
  };
  expression: string;
  externalId: string;
};

const testTypes = [
  {
    key: 'GENERAL',
    value: InternalApiJMESPathExpressionTestType.GENERAL,
    label: 'General',
  },
  {
    key: 'SCENARIO',
    value: InternalApiJMESPathExpressionTestType.SCENARIO,
    label: 'Scenario',
  },
  {
    key: 'SEGMENT',
    value: InternalApiJMESPathExpressionTestType.SEGMENT,
    label: 'Segment',
  },
];

const insertScripts = [
  ['IS_FIRST_COUNTERPARTY_EXPRESSION', 'TRANSACTION'],
  ['IS_SECOND_COUNTERPARTY_EXPRESSION', 'TRANSACTION'],
  ['IS_SERVICE_PROVIDER_CHECK_EXPRESSION', 'SERVICE_PROVIDER'],
].map((it) => ({
  key: it[0],
  value: it[0],
  label: it[0],
  type: it[1],
}));

const entityTypes = [
  {
    key: 'PERSON',
    value: InternalApiEntityType.PERSON,
    label: 'Person',
  },
  {
    key: 'TRANSACTION',
    value: InternalApiEntityType.TRANSACTION,
    label: 'Transaction',
  },
];

const attemptJSONPrettyPrint = (input: string): React.JSX.Element => {
  try {
    return <pre>{JSON.stringify(JSON.parse(input), null, 2)}</pre>;
  } catch (_) {
    return <>{input}</>;
  }
};

export const JMESPathSandbox = () => {
  const [result, setResult] = useState<InternalApiJMESPathExpressionTestResult>();
  const [expressionRan, setExpressionRan] = useState(false);
  const { currentOrganisation } = useMe();

  const initialValues: TestInputPanelValues = {
    testType: testTypes[0],
    entityType: entityTypes[0],
    insertScript: insertScripts[0],
    externalId: '',
    expression: '',
  };

  const submit = async (
    values: TestInputPanelValues,
    { setStatus, setSubmitting }: FormikHelpers<TestInputPanelValues>
  ) => {
    try {
      const request = {
        internalApiJMESPathExpressionTestRequest: {
          testType: values.testType.value,
          expression: values.expression,
          externalId: values.externalId,
          entityType: values.entityType.value,
        },
      };
      const results = await jmespathExpressionTestApi.testExpression(request);
      setResult(results);
    } catch (e) {
      setResult(undefined);
      setStatus(await parseErrorMessage(e));
    }
    setExpressionRan(true);
    setSubmitting(false);
  };

  const createScript = (values: TestInputPanelValues) => {
    if (!currentOrganisation) {
      throw Error('Organisation not selected');
    }
    const query = generateInsertScript(
      currentOrganisation.id!,
      values.expression,
      values.insertScript.type,
      values.insertScript.key
    );
    const queryAsBlob = new Blob([query], {
      type: 'text/plain',
    });
    const filename = `org_${currentOrganisation.id}_insert_${values.insertScript.key}.sql`;
    downloadDocument(queryAsBlob, filename);
  };

  return (
    <SalvPage title={'JMESPath Sandbox'}>
      <Formik
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={submit}
        initialValues={initialValues}
      >
        {({ isSubmitting, values, setFieldValue, resetForm }: FormikProps<TestInputPanelValues>) => {
          return (
            <Form>
              <Card>
                <Card.Body>
                  <Row>
                    <Col>
                      <FormField
                        type='textarea'
                        name='expression'
                        label='JMESPath expression'
                        placeholder='JMESPath expression'
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <SalvTooltipWrapper
                        tooltip={'Use the General expression type to test Counter Party and Service Provider logic'}
                        isDisabled={values.testType.key !== InternalApiJMESPathExpressionTestType.GENERAL}
                      >
                        <SalvDropdown
                          label={'Expression Type'}
                          options={testTypes}
                          value={values.testType}
                          clearable={false}
                          onChange={([value]) => {
                            setFieldValue('testType', value);
                          }}
                        />
                      </SalvTooltipWrapper>
                    </Col>
                    <Col>
                      <SalvDropdown
                        label={'Entity Type'}
                        options={entityTypes}
                        value={values.entityType}
                        clearable={false}
                        onChange={([value]) => {
                          setFieldValue('entityType', value);
                        }}
                      />
                    </Col>
                    <Col>
                      <FormField name='externalId' label='External ID' placeholder='External ID' />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <SalvDropdown
                        label={'Insert script'}
                        disabled={!expressionRan}
                        options={insertScripts}
                        value={values.insertScript}
                        clearable={false}
                        onChange={([value]) => {
                          setFieldValue('insertScript', value);
                        }}
                      />
                    </Col>
                    <Col xs={8} style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <Button
                        onClick={() => {
                          createScript(values);
                        }}
                        disabled={!expressionRan}
                        variant='primary'
                        className='m-b-4'
                      >
                        Generate insert script
                      </Button>
                      <span className='p-l-8 m-b-8'>
                        For organisation: {currentOrganisation?.name}({currentOrganisation?.id})
                      </span>
                    </Col>
                  </Row>
                </Card.Body>
                <Card.Footer className='text-end'>
                  <ReactForm.Group className='form-group mt-4'>
                    <Button
                      variant='outline-secondary'
                      onClick={() => {
                        resetForm();
                        setExpressionRan(false);
                        setResult(undefined);
                      }}
                      className='me-3'
                    >
                      Clear
                    </Button>
                    <Button disabled={isSubmitting} type='submit' variant='primary'>
                      Search
                    </Button>
                  </ReactForm.Group>
                </Card.Footer>
              </Card>
            </Form>
          );
        }}
      </Formik>
      <Card>
        {result ? (
          <Table striped size='sm' className='card-table'>
            <thead>
              <tr>
                <th>Expression result</th>
                <th>Entity</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{attemptJSONPrettyPrint(result.expressionResult)}</td>
                <td>{attemptJSONPrettyPrint(result.entity)}</td>
              </tr>
            </tbody>
          </Table>
        ) : (
          <span>No results...</span>
        )}
      </Card>
    </SalvPage>
  );
};

export default JMESPathSandbox;
